(() => {
  const GRID_SELECTOR = '#product-grid[data-in-stock-first="true"]';
  const ITEM_SELECTOR = ':scope > li[data-product-available]';
  const initialized = new WeakSet();

  const isAvailable = (item) => item.dataset.productAvailable === 'true';

  function sortCurrentPage(grid) {
    const items = Array.from(grid.querySelectorAll(ITEM_SELECTOR));
    const available = items.filter(isAvailable);
    const soldOut = items.filter((item) => !isAvailable(item));
    grid.replaceChildren(...available, ...soldOut);
  }

  function pageUrl(page) {
    const url = new URL(window.location.href);
    if (page === 1) url.searchParams.delete('page');
    else url.searchParams.set('page', String(page));
    return url.toString();
  }

  async function fetchPageItems(page) {
    const response = await fetch(pageUrl(page), {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    });
    if (!response.ok) throw new Error(`Collection page ${page}: ${response.status}`);

    const documentCopy = new DOMParser().parseFromString(await response.text(), 'text/html');
    const grid = documentCopy.querySelector(GRID_SELECTOR);
    if (!grid) throw new Error(`Collection page ${page}: product grid not found`);
    return Array.from(grid.querySelectorAll(ITEM_SELECTOR));
  }

  async function fetchWithConcurrency(pages, concurrency = 4) {
    const results = new Map();
    let cursor = 0;

    async function worker() {
      while (cursor < pages.length) {
        const page = pages[cursor++];
        results.set(page, await fetchPageItems(page));
      }
    }

    await Promise.all(
      Array.from({ length: Math.min(concurrency, pages.length) }, () => worker())
    );
    return results;
  }

  async function initialize(grid) {
    if (initialized.has(grid)) return;
    initialized.add(grid);

    // Make the visible page useful immediately while the remaining pages load.
    sortCurrentPage(grid);

    const totalPages = Number(grid.dataset.totalPages || 1);
    const currentPage = Number(grid.dataset.currentPage || 1);
    const pageSize = Number(grid.dataset.productsPerPage || grid.children.length);
    if (totalPages <= 1 || pageSize <= 0) return;

    try {
      const pages = Array.from({ length: totalPages }, (_, index) => index + 1);
      const fetched = await fetchWithConcurrency(pages);
      const allItems = pages.flatMap((page) => fetched.get(page) || []);
      const available = allItems.filter(isAvailable);
      const soldOut = allItems.filter((item) => !isAvailable(item));
      const sorted = available.concat(soldOut);
      const start = (currentPage - 1) * pageSize;
      const visible = sorted.slice(start, start + pageSize);

      if (visible.length) grid.replaceChildren(...visible);
      grid.dataset.inStockSorted = 'true';
    } catch (error) {
      // The synchronous current-page ordering remains as a safe fallback.
      console.warn('[in-stock-first]', error);
    }
  }

  function initializeAll(root = document) {
    if (root.matches?.(GRID_SELECTOR)) initialize(root);
    root.querySelectorAll?.(GRID_SELECTOR).forEach(initialize);
  }

  initializeAll();

  // Shopify's filter/sort UI replaces the grid through Ajax, so initialize replacements too.
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === Node.ELEMENT_NODE) initializeAll(node);
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
